import { useEffect, useRef, useState } from "react"
import { products_search_path } from "@/lib/routes"
import { cn } from "@/lib/utils"

type ProductHit = { external_id: string; short_name: string | null; name: string }

interface Props {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void // Ctrl+Enter or Cmd+Enter; plain Enter starts a new line
  placeholder?: string
}

// Multi-line text box where typing # and two letters searches the org's
// products (the top bar search). Picking one writes "#<product id> " into
// the text, which NoteText later shows as a link.
export default function MentionInput({ value, onChange, onSubmit, placeholder }: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [query, setQuery] = useState<string | null>(null)
  const [matches, setMatches] = useState<ProductHit[]>([])
  const [highlight, setHighlight] = useState(0)

  useEffect(() => {
    const term = query?.trim() ?? ""
    if (term.length < 2) { setMatches([]); return }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      fetch(products_search_path({ q: term }), { headers: { Accept: "application/json" }, signal: controller.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((hits: ProductHit[]) => { setMatches(hits.slice(0, 6)); setHighlight(0) })
        .catch((e) => { if (e.name !== "AbortError") setMatches([]) })
    }, 200)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query])

  const readQuery = (text: string, caret: number) => {
    const found = text.slice(0, caret).match(/#([^\s#]*)$/)
    setQuery(found ? found[1] : null)
  }

  const close = () => { setQuery(null); setMatches([]) }

  const pick = (id: string) => {
    const input = inputRef.current
    const caret = input?.selectionStart ?? value.length
    const before = value.slice(0, caret).replace(/#[^\s#]*$/, `#${id} `)
    onChange(before + value.slice(caret))
    close()
    requestAnimationFrame(() => input?.setSelectionRange(before.length, before.length))
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (matches.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((highlight + 1) % matches.length); return }
      if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((highlight - 1 + matches.length) % matches.length); return }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[highlight].external_id); return }
      if (e.key === "Escape") { e.preventDefault(); close(); return }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit() }
  }

  return (
    <div className="relative">
      <textarea
        ref={inputRef}
        rows={3}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); readQuery(e.target.value, e.target.selectionStart ?? 0) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(close, 150)}
        className="block w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring"
      />
      {matches.length > 0 && query !== null && (
        <div className="absolute left-0 top-full z-50 mt-1 w-96 rounded-md border bg-popover p-1 shadow-md">
          {matches.map((p, i) => (
            <div
              key={p.external_id}
              onMouseDown={(e) => { e.preventDefault(); pick(p.external_id) }}
              onMouseEnter={() => setHighlight(i)}
              className={cn("flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm", i === highlight && "bg-accent")}
            >
              <span className="shrink-0 font-medium">{p.short_name ?? `#${p.external_id}`}</span>
              <span className="truncate">{p.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
